import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Upload,
  Download,
  Edit,
  Trash2,
  FileText,
  Search,
  FolderOpen,
  Loader2,
  File,
  FileImage,
  FileSpreadsheet,
  Package,
  Plane,
  Ship,
  Calendar,
  X,
  AlertTriangle,
  Eye,
  RotateCcw,
  Clock,
  User,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function DocumentManagement() {
  const [documents, setDocuments] = useState([]);
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [filters, setFilters] = useState({ contenedores: [], awbs: [], bls: [], lotes: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("documents"); // "documents" o "trash"
  
  // Filtros activos
  const [filterType, setFilterType] = useState("all"); // all, contenedor, awb, bl, lote, fecha
  const [filterValue, setFilterValue] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newFileName, setNewFileName] = useState("");
  
  // Upload form
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadContenedor, setUploadContenedor] = useState("");
  const [uploadAwb, setUploadAwb] = useState("");
  const [uploadBl, setUploadBl] = useState("");
  const [uploadLote, setUploadLote] = useState("");
  const [uploadFileName, setUploadFileName] = useState("");
  // Estado para el buscador de lotes
  const [loteSearchTerm, setLoteSearchTerm] = useState("");
  const [showLoteDropdown, setShowLoteDropdown] = useState(false);
  const loteDropdownRef = useRef(null);
  
  // Cerrar dropdown de lotes al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (loteDropdownRef.current && !loteDropdownRef.current.contains(event.target)) {
        setShowLoteDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const [uploadTipoDoc, setUploadTipoDoc] = useState("");
  
  // Dialog de confirmación de sobreescritura
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  
  // Dialog de restaurar/eliminar permanentemente
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  
  const { hasRole, user } = useAuth();

  // Tipos de documento para catalogar (sin "certificado" ni "factura_destino")
  const allTiposDocumento = [
    { value: "factura_origen", label: "Factura Origen" },
    { value: "factura_cliente", label: "Factura Cliente" },
    { value: "packing_list", label: "Packing List" },
    { value: "etiqueta", label: "Etiqueta" },
    { value: "dua", label: "DUA" },
    { value: "certificado_exportacion", label: "Certificado de exportación" },
    { value: "nota_valor", label: "Nota de Valor" },
    { value: "cso", label: "CSO (Sanitario)" },
    { value: "anexo_iv", label: "Anexo IV" },
    { value: "certificado_captura", label: "Certificado de captura" },
    { value: "bl", label: "BL" },
    { value: "awb", label: "AWB (Guía Aérea)" },
    { value: "otros", label: "Otros" },
  ];

  const isAdmin = hasRole("admin");
  const isProveedor = hasRole("proveedor");
  const isCliente = hasRole("cliente");
  const canUpload = isAdmin || isProveedor; // Cliente NO puede subir documentos
  const canRename = isAdmin;
  const canDelete = isAdmin || isProveedor; // Admin y Proveedor pueden borrar
  
  // Filtrar tipos de documento para el dropdown de subida según rol
  // Proveedor: NO puede subir factura_cliente
  const tiposDocumento = isProveedor 
    ? allTiposDocumento.filter(t => t.value !== "factura_cliente")
    : allTiposDocumento;
  
  // Filtrar tipos de documento visibles según rol
  // Proveedor: ve todo EXCEPTO factura_cliente
  // Cliente: ve los documentos especificados para su acceso
  const allowedDocTypes = isCliente 
    ? [
        "factura_cliente",        // Factura Cliente
        "packing_list",           // Packing List
        "etiqueta",               // Etiqueta
        "certificado_exportacion", // Certificado de exportación
        "cso",                    // CSO (Sanitario)
        "certificado_captura",    // Certificado de captura
        "bl",                     // BL
        "awb"                     // AWB
      ]
    : isProveedor 
      ? null // Proveedor: ve todos (el backend ya filtra factura_cliente)
      : null; // Admin: todos

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      if (filterType === "contenedor" && filterValue) {
        params.append("contenedor", filterValue);
      } else if (filterType === "awb" && filterValue) {
        params.append("awb", filterValue);
      } else if (filterType === "bl" && filterValue) {
        params.append("bl", filterValue);
      } else if (filterType === "lote" && filterValue) {
        params.append("numero_lote", filterValue);
      } else if (filterType === "fecha") {
        if (fechaDesde) params.append("fecha_desde", fechaDesde);
        if (fechaHasta) params.append("fecha_hasta", fechaHasta);
      }
      
      const queryString = params.toString();
      const response = await api.get(`/documents${queryString ? `?${queryString}` : ""}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error("Error al cargar los documentos");
    }
  }, [filterType, filterValue, fechaDesde, fechaHasta]);

  const fetchDeletedDocuments = useCallback(async () => {
    if (!isAdmin && !isProveedor) return;
    try {
      const response = await api.get("/deleted-documents");
      setDeletedDocuments(response.data);
    } catch (error) {
      console.error("Error fetching deleted documents:", error);
    }
  }, [isAdmin, isProveedor]);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await api.get("/documents/filters");
      setFilters(response.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDocuments(), fetchFilters(), fetchDeletedDocuments()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [filterType, filterValue, fechaDesde, fechaHasta, fetchDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error("Selecciona un archivo");
      return;
    }
    if (!uploadLote) {
      toast.error("Debes seleccionar un lote");
      return;
    }
    if (!uploadTipoDoc) {
      toast.error("Debes seleccionar un tipo de documento");
      return;
    }

    // Tipos que permiten múltiples documentos (no verificar duplicados)
    const tiposMultiples = ["etiqueta", "otros"];
    const skipDuplicateCheck = tiposMultiples.includes(uploadTipoDoc) || uploadTipoDoc.startsWith("etiqueta");

    // Verificar si ya existe un documento del mismo tipo (excepto etiquetas y otros)
    if (!skipDuplicateCheck) {
      try {
        const checkRes = await api.get(`/documents/check-duplicate?numero_lote=${uploadLote}&tipo_documento=${uploadTipoDoc}`);
        if (checkRes.data.exists) {
          // Guardar datos para la subida pendiente
          setPendingUpload({ uploadFile, uploadLote, uploadTipoDoc });
          setExistingDocument(checkRes.data.document);
          setOverwriteDialogOpen(true);
          return;
        }
      } catch (error) {
        console.error("Error verificando duplicados:", error);
      }
    }

    // Proceder con la subida
    await executeUpload();
  };

  const executeUpload = async (overwrite = false) => {
    // Obtener datos del lote seleccionado
    const loteToUse = pendingUpload?.uploadLote || uploadLote;
    const fileToUse = pendingUpload?.uploadFile || uploadFile;
    const tipoToUse = pendingUpload?.uploadTipoDoc || uploadTipoDoc;
    
    const selectedLoteData = filters.lotes.find(l => l.value === loteToUse);
    
    // Generar nombre automático según el formato especificado
    let generatedFileName = "";
    const originalName = fileToUse.name;
    const extension = originalName.substring(originalName.lastIndexOf('.'));
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.'));
    
    // Fecha del lote (desde created_at - fecha de creación del lote)
    const fechaLote = selectedLoteData?.created_at 
      ? new Date(selectedLoteData.created_at).toISOString().slice(0, 10).replace(/-/g, '')
      : new Date().toISOString().slice(0, 10).replace(/-/g, '');
    
    const facturaOrigen = selectedLoteData?.factura_origen || "";
    const numeroLote = loteToUse;
    const blValue = selectedLoteData?.bl || uploadBl || "";
    const contenedorValue = selectedLoteData?.contenedor || uploadContenedor || "";
    
    // Mapeo de tipos a nombres legibles
    const tipoNombres = {
      "factura_origen": "Factura Origen",
      "factura_cliente": "Factura Cliente",
      "packing_list": "Packing List",
      "etiqueta": "Etiqueta",
      "dua": "DUA",
      "certificado_exportacion": "Certificado de Exportación",
      "nota_valor": "Nota de Valor",
      "cso": "CSO",
      "certificado_captura": "Certificado de Captura",
      "bl": "BL",
      "awb": "AWB",
      "anexo_iv": "Anexo IV",
      "otros": nameWithoutExt
    };
    
    const tipoNombre = tipoNombres[tipoToUse] || nameWithoutExt;
    
    // Formato: AAAAMMDD [TipoDoc/NombreOriginal] [FacturaOrigen] [Lote] [BL] [Contenedor].extension
    const parts = [fechaLote, tipoNombre];
    if (facturaOrigen) parts.push(facturaOrigen);
    parts.push(numeroLote);
    if (blValue) parts.push(blValue);
    if (contenedorValue) parts.push(contenedorValue);
    
    generatedFileName = parts.join(' ') + extension;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", fileToUse);
    if (contenedorValue) formData.append("contenedor", contenedorValue);
    if (uploadAwb || selectedLoteData?.awb) formData.append("awb", uploadAwb || selectedLoteData?.awb || "");
    if (blValue) formData.append("bl", blValue);
    formData.append("numero_lote", loteToUse);
    formData.append("nombre_archivo", generatedFileName);
    if (tipoToUse) formData.append("tipo_documento", tipoToUse);

    try {
      // El backend maneja la sobrescritura directamente con el parámetro overwrite
      // No necesitamos eliminar primero el documento existente
      if (overwrite) {
        formData.append("overwrite", "true");
      }
      
      await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(overwrite ? "Documento sobreescrito correctamente" : "Documento subido correctamente");
      setUploadDialogOpen(false);
      setOverwriteDialogOpen(false);
      resetUploadForm();
      setPendingUpload(null);
      setExistingDocument(null);
      fetchDocuments();
      fetchFilters();
      fetchDeletedDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir el documento");
    } finally {
      setUploading(false);
    }
  };

  const handleOverwriteConfirm = () => {
    executeUpload(true);
  };

  const handleOverwriteCancel = () => {
    setOverwriteDialogOpen(false);
    setPendingUpload(null);
    setExistingDocument(null);
  };

  // Cuando se selecciona un lote, auto-rellenar los campos relacionados
  const handleLoteSelect = (loteValue) => {
    setUploadLote(loteValue);
    const selectedLoteData = filters.lotes.find(l => l.value === loteValue);
    if (selectedLoteData) {
      setUploadContenedor(selectedLoteData.contenedor || "");
      setUploadAwb(selectedLoteData.awb || "");
      setUploadBl(selectedLoteData.bl || "");
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadContenedor("");
    setUploadAwb("");
    setUploadBl("");
    setUploadLote("");
    setUploadFileName("");
    setUploadTipoDoc("");
    setLoteSearchTerm("");
    setShowLoteDropdown(false);
  };

  const getTipoDocLabel = (tipo) => {
    const found = tiposDocumento.find(t => t.value === tipo);
    return found ? found.label : tipo || "-";
  };

  const handleDownload = async (doc) => {
    // Para documentos del sistema sin archivo físico ni GridFS, redirigir a la página correspondiente
    if (doc.es_documento_sistema && !doc.ruta_archivo && !doc.gridfs_id) {
      if (doc.tipo_documento === "factura_origen" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/invoice-origin`, "_blank");
        return;
      }
      if (doc.tipo_documento === "packing_list" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/packing-list`, "_blank");
        return;
      }
      toast.info("Este documento se genera al visualizarlo desde el lote");
      return;
    }
    
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      
      const blob = new Blob([response.data], { type: doc.tipo_archivo });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.nombre_archivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar el documento");
    }
  };

  const handleRename = async () => {
    if (!selectedDoc || !newFileName.trim()) return;

    try {
      await api.put(`/documents/${selectedDoc.id}/rename`, {
        nombre_archivo: newFileName.trim(),
      });
      toast.success("Documento renombrado correctamente");
      setRenameDialogOpen(false);
      setSelectedDoc(null);
      setNewFileName("");
      fetchDocuments();
    } catch (error) {
      toast.error("Error al renombrar el documento");
    }
  };

  const handleDelete = async () => {
    if (!selectedDoc) return;

    try {
      await api.delete(`/documents/${selectedDoc.id}`);
      toast.success("Documento movido a la papelera");
      setDeleteDialogOpen(false);
      setSelectedDoc(null);
      fetchDocuments();
      fetchFilters();
      fetchDeletedDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el documento");
    }
  };

  const handleRestore = async () => {
    if (!selectedDoc) return;

    try {
      await api.post(`/deleted-documents/${selectedDoc.id}/restore`);
      toast.success("Documento restaurado correctamente");
      setRestoreDialogOpen(false);
      setSelectedDoc(null);
      fetchDocuments();
      fetchDeletedDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar el documento");
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedDoc) return;

    try {
      await api.delete(`/deleted-documents/${selectedDoc.id}/permanent`);
      toast.success("Documento eliminado permanentemente");
      setPermanentDeleteDialogOpen(false);
      setSelectedDoc(null);
      fetchDeletedDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar permanentemente");
    }
  };

  const handleEmptyTrash = async () => {
    // Doble confirmación
    const count = deletedDocuments.length;
    const confirm1 = window.confirm(
      `⚠️ ADVERTENCIA: Vas a eliminar PERMANENTEMENTE ${count} documento(s) de la papelera.\n\nEsta acción NO se puede deshacer.\n\n¿Deseas continuar?`
    );
    
    if (!confirm1) return;
    
    const confirm2 = window.confirm(
      `🚨 ÚLTIMA CONFIRMACIÓN 🚨\n\n¿Estás ABSOLUTAMENTE SEGURO de que quieres eliminar ${count} documento(s) PARA SIEMPRE?\n\nEscribe "VACIAR" mentalmente y pulsa Aceptar.`
    );
    
    if (!confirm2) return;
    
    setEmptyingTrash(true);
    try {
      const response = await api.delete('/deleted-documents/empty-trash');
      toast.success(`Papelera vaciada: ${response.data.deleted_count} documentos eliminados`);
      fetchDeletedDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al vaciar la papelera");
    } finally {
      setEmptyingTrash(false);
    }
  };

  const openRestoreDialog = (doc) => {
    setSelectedDoc(doc);
    setRestoreDialogOpen(true);
  };

  const openPermanentDeleteDialog = (doc) => {
    setSelectedDoc(doc);
    setPermanentDeleteDialogOpen(true);
  };

  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return 0;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const handlePreview = async (doc) => {
    // Verificar si el documento tiene archivo físico (en disco o en GridFS)
    const tieneArchivo = doc.ruta_archivo || doc.file_url || doc.file_path || doc.gridfs_id;
    
    // Si NO tiene archivo físico, intentar redirigir a la generación dinámica
    if (!tieneArchivo) {
      // Documentos que se pueden generar dinámicamente
      if (doc.tipo_documento === "factura_origen" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/invoice-origin`, "_blank");
        return;
      }
      if (doc.tipo_documento === "factura_cliente" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/invoice-client`, "_blank");
        return;
      }
      if (doc.tipo_documento === "packing_list" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/packing-list`, "_blank");
        return;
      }
      if (doc.tipo_documento === "etiqueta" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/labels`, "_blank");
        return;
      }
      if (doc.tipo_documento === "cso" && doc.batch_id) {
        window.open(`/batches/${doc.batch_id}/informe-cso`, "_blank");
        return;
      }
      
      // Para documentos que NO se pueden generar dinámicamente
      toast.error(
        "Este documento no tiene archivo físico guardado. " +
        "Debe subirse nuevamente desde la gestión documental."
      );
      return;
    }
    
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      
      const blob = new Blob([response.data], { type: doc.tipo_archivo || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      
      // Si es PDF o imagen, mostrar en modal de previsualización
      const isPdf = doc.tipo_archivo?.includes("pdf") || doc.nombre_archivo?.toLowerCase().endsWith(".pdf");
      const isImage = doc.tipo_archivo?.startsWith("image/") || 
        /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.nombre_archivo);
      
      if (isPdf || isImage) {
        setPreviewUrl(url);
        setPreviewDoc(doc);
        setPreviewDialogOpen(true);
      } else {
        // Para otros tipos de archivo, abrir en nueva pestaña
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error al previsualizar:", error);
      if (error.response?.status === 404) {
        toast.error(
          "El archivo no existe en el servidor. " +
          "Debe subirse nuevamente desde la gestión documental."
        );
      } else {
        toast.error("Error al previsualizar el documento");
      }
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewDoc(null);
    setPreviewDialogOpen(false);
  };

  // Previsualizar documento de la papelera
  const handlePreviewDeleted = async (doc) => {
    // Si no tiene archivo físico ni en GridFS, mostrar mensaje
    if (!doc.ruta_archivo && !doc.gridfs_id) {
      toast.info("Este documento no tiene archivo físico para previsualizar");
      return;
    }
    
    try {
      const response = await api.get(`/deleted-documents/${doc.id}/download`, {
        responseType: "blob",
      });
      
      const blob = new Blob([response.data], { type: doc.tipo_archivo || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      
      const isPdf = doc.tipo_archivo?.includes("pdf") || doc.nombre_archivo?.toLowerCase().endsWith(".pdf");
      const isImage = doc.tipo_archivo?.startsWith("image/") || 
        /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.nombre_archivo);
      
      if (isPdf || isImage) {
        setPreviewUrl(url);
        setPreviewDoc(doc);
        setPreviewDialogOpen(true);
      } else {
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error al previsualizar:", error);
      toast.error("Error al previsualizar el documento");
    }
  };

  const openRenameDialog = (doc) => {
    setSelectedDoc(doc);
    setNewFileName(doc.nombre_archivo);
    setRenameDialogOpen(true);
  };

  const openDeleteDialog = (doc) => {
    setSelectedDoc(doc);
    setDeleteDialogOpen(true);
  };

  const clearFilters = () => {
    setFilterType("all");
    setFilterValue("");
    setFechaDesde("");
    setFechaHasta("");
    setSearchTerm("");
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (tipo, tipoDoc) => {
    if (tipoDoc === "factura_origen" || tipoDoc === "factura_destino") {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    if (!tipo) return <File className="w-5 h-5 text-slate-400" />;
    if (tipo.includes("image")) return <FileImage className="w-5 h-5 text-blue-500" />;
    if (tipo.includes("spreadsheet") || tipo.includes("excel") || tipo.includes("csv")) {
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    }
    if (tipo.includes("pdf")) return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-slate-400" />;
  };

  const filteredDocuments = documents.filter((doc) => {
    // Primero filtrar por rol
    if (allowedDocTypes) {
      // Para proveedor: mostrar documentos permitidos + los subidos por su usuario
      if (isProveedor) {
        const isAllowedType = allowedDocTypes.includes(doc.tipo_documento) || 
          allowedDocTypes.some(t => doc.tipo_documento?.startsWith(t));
        const isOwnUpload = doc.uploaded_by === user?.id;
        if (!isAllowedType && !isOwnUpload) return false;
      } else if (isCliente) {
        // Para cliente: solo documentos de tipos permitidos
        // Incluir variantes que empiecen con el tipo base (ej: etiqueta_T4_21.06kg)
        const isAllowedType = allowedDocTypes.includes(doc.tipo_documento) || 
          allowedDocTypes.some(t => doc.tipo_documento?.startsWith(t));
        if (!isAllowedType) return false;
      }
    }
    
    // Luego filtrar por búsqueda
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      doc.nombre_archivo?.toLowerCase().includes(term) ||
      doc.contenedor?.toLowerCase().includes(term) ||
      doc.awb?.toLowerCase().includes(term) ||
      doc.bl?.toLowerCase().includes(term) ||
      doc.numero_lote?.toLowerCase().includes(term)
    );
  });

  const hasActiveFilters = filterType !== "all" || searchTerm || fechaDesde || fechaHasta;

  // Determinar si el usuario puede ver la papelera (admin o proveedor)
  const canViewTrash = isAdmin || isProveedor;

  return (
    <div className="space-y-6" data-testid="document-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestión Documental</h1>
          <p className="text-slate-500 mt-1">
            Documentos organizados por contenedor, AWB, BL o lote
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canViewTrash && (
            <div className="flex items-center bg-slate-100 rounded-lg p-1 border border-slate-200">
              <button
                onClick={() => setActiveTab("documents")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "documents"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="tab-documents"
              >
                <FileText className="w-4 h-4 inline-block mr-2" />
                Documentos
              </button>
              <button
                onClick={() => setActiveTab("trash")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "trash"
                    ? "bg-white text-red-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="tab-trash"
              >
                <Trash2 className="w-4 h-4 inline-block mr-2" />
                Papelera
                {deletedDocuments.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full font-bold">
                    {deletedDocuments.length}
                  </span>
                )}
              </button>
            </div>
          )}
          {canUpload && activeTab === "documents" && (
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="upload-document-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              Subir Documento
            </Button>
          )}
        </div>
      </div>

      {/* Contenido según pestaña activa */}
      {activeTab === "documents" ? (
        <>
          {/* Filtros */}
          <div className="bg-white p-4 rounded-sm border border-slate-200 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Tipo de filtro */}
          <div className="w-48">
            <Label className="text-xs text-slate-500 mb-1">Filtrar por</Label>
            <Select value={filterType} onValueChange={(v) => { setFilterType(v); setFilterValue(""); }}>
              <SelectTrigger data-testid="filter-type">
                <SelectValue placeholder="Seleccionar filtro" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="contenedor">
                  <span className="flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Contenedor</span>
                </SelectItem>
                <SelectItem value="awb">
                  <span className="flex items-center gap-2"><Plane className="w-4 h-4" /> AWB (Aéreo)</span>
                </SelectItem>
                <SelectItem value="bl">
                  <span className="flex items-center gap-2"><Ship className="w-4 h-4" /> B/L (Marítimo)</span>
                </SelectItem>
                <SelectItem value="lote">
                  <span className="flex items-center gap-2"><Package className="w-4 h-4" /> Nº Lote</span>
                </SelectItem>
                <SelectItem value="fecha">
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Fecha</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Valor del filtro según tipo */}
          {filterType === "contenedor" && filters.contenedores.length > 0 && (
            <div className="w-56">
              <Label className="text-xs text-slate-500 mb-1">Contenedor</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar contenedor" />
                </SelectTrigger>
                <SelectContent>
                  {filters.contenedores.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.value} ({c.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === "awb" && filters.awbs.length > 0 && (
            <div className="w-56">
              <Label className="text-xs text-slate-500 mb-1">AWB (Guía Aérea)</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar AWB" />
                </SelectTrigger>
                <SelectContent>
                  {filters.awbs.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.value} ({a.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === "bl" && filters.bls.length > 0 && (
            <div className="w-56">
              <Label className="text-xs text-slate-500 mb-1">B/L (Bill of Lading)</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar B/L" />
                </SelectTrigger>
                <SelectContent>
                  {filters.bls.map((b) => (
                    <SelectItem key={b.value} value={b.value}>
                      {b.value} ({b.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === "lote" && filters.lotes.length > 0 && (
            <div className="w-56">
              <Label className="text-xs text-slate-500 mb-1">Número de Lote</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar lote" />
                </SelectTrigger>
                <SelectContent>
                  {filters.lotes.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.value} ({l.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === "fecha" && (
            <>
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1">Desde</Label>
                <Input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1">Hasta</Label>
                <Input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Búsqueda por texto */}
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-500 mb-1">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-documents"
              />
            </div>
          </div>

          {/* Limpiar filtros */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
      </div>

      {/* Tabla de documentos */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            No hay documentos
          </h3>
          <p className="text-slate-500 mb-4">
            {hasActiveFilters
              ? "No se encontraron documentos con estos filtros"
              : "Comienza subiendo tu primer documento"}
          </p>
          {canUpload && !hasActiveFilters && (
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Upload className="w-4 h-4 mr-2" />
              Subir Documento
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[50px]">
                  Tipo
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Documento
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nombre
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Contenedor
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  AWB
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  B/L
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Lote
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Fecha
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[120px]">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow
                  key={doc.id}
                  className="hover:bg-slate-50"
                  data-testid={`document-row-${doc.id}`}
                >
                  <TableCell>{getFileIcon(doc.tipo_archivo, doc.tipo_documento)}</TableCell>
                  <TableCell>
                    <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                      {getTipoDocLabel(doc.tipo_documento)}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div>
                      <p className="whitespace-nowrap" title={doc.nombre_archivo}>{doc.nombre_archivo}</p>
                      {doc.es_documento_sistema && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                          Sistema
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {doc.contenedor ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs font-mono">
                        <FolderOpen className="w-3 h-3" />
                        {doc.contenedor}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {doc.awb ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs font-mono">
                        <Plane className="w-3 h-3" />
                        {doc.awb}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {doc.bl ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono">
                        <Ship className="w-3 h-3" />
                        {doc.bl}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {doc.numero_lote ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-mono">
                        <Package className="w-3 h-3" />
                        {doc.numero_lote}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {doc.created_at
                      ? format(new Date(doc.created_at), "dd/MM/yyyy", { locale: es })
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                        onClick={() => handlePreview(doc)}
                        title="Previsualizar"
                        data-testid={`preview-doc-${doc.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-600 hover:text-blue-700"
                        onClick={() => handleDownload(doc)}
                        title="Descargar"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {canRename && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openRenameDialog(doc)}
                          title="Renombrar"
                          data-testid={`rename-doc-${doc.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => openDeleteDialog(doc)}
                          title="Eliminar"
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
        </>
      ) : (
        /* Vista de Papelera */
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-800">Papelera de Reciclaje</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Los documentos eliminados permanecen aquí durante 30 días antes de ser eliminados permanentemente.
                    {isAdmin 
                      ? " Puedes restaurarlos o eliminarlos definitivamente."
                      : " Puedes restaurar los documentos de tus lotes."}
                  </p>
                </div>
              </div>
              {isAdmin && deletedDocuments.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleEmptyTrash}
                  disabled={emptyingTrash}
                  className="shrink-0"
                  data-testid="empty-trash-btn"
                >
                  {emptyingTrash ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Vaciando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Vaciar Papelera ({deletedDocuments.length})
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {deletedDocuments.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
              <Trash2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Papelera vacía
              </h3>
              <p className="text-slate-500">
                No hay documentos eliminados
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[50px]">
                      Tipo
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                      Documento
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                      Nombre
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                      Lote
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                      Eliminado por
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                      Fecha eliminación
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                      Expira en
                    </TableHead>
                    <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[120px]">
                      Acciones
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deletedDocuments.map((doc) => {
                    const daysLeft = getDaysUntilExpiry(doc.expires_at);
                    return (
                      <TableRow
                        key={doc.id}
                        className="hover:bg-slate-50"
                        data-testid={`deleted-doc-row-${doc.id}`}
                      >
                        <TableCell>{getFileIcon(doc.tipo_archivo, doc.tipo_documento)}</TableCell>
                        <TableCell>
                          <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                            {getTipoDocLabel(doc.tipo_documento)}
                          </span>
                          {doc.deleted_reason === "sobrescrito" && (
                            <span className="ml-2 text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded">
                              Sobrescrito
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <p className="whitespace-nowrap" title={doc.nombre_archivo}>
                            {doc.nombre_archivo}
                          </p>
                        </TableCell>
                        <TableCell>
                          {doc.numero_lote ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-mono">
                              <Package className="w-3 h-3" />
                              {doc.numero_lote}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium">{doc.deleted_by_name}</p>
                              <p className="text-xs text-slate-500 capitalize">{doc.deleted_by_role}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {doc.deleted_at
                            ? format(new Date(doc.deleted_at), "dd/MM/yyyy HH:mm", { locale: es })
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 text-sm ${daysLeft <= 3 ? "text-red-600" : "text-slate-600"}`}>
                            <Clock className="w-4 h-4" />
                            {daysLeft} días
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:text-blue-700"
                              onClick={() => handlePreviewDeleted(doc)}
                              title="Ver documento"
                              data-testid={`preview-deleted-doc-${doc.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700"
                              onClick={() => openRestoreDialog(doc)}
                              title="Restaurar"
                              data-testid={`restore-doc-${doc.id}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                                onClick={() => openPermanentDeleteDialog(doc)}
                                title="Eliminar permanentemente"
                                data-testid={`permanent-delete-doc-${doc.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Dialog de subida */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="upload-dialog-description">
          <DialogHeader>
            <DialogTitle>Subir Documento</DialogTitle>
            <p id="upload-dialog-description" className="sr-only">Formulario para subir un nuevo documento al sistema</p>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            {/* Selección de Lote con búsqueda dinámica */}
            <div className="space-y-2">
              <Label>Número de Lote *</Label>
              <div className="relative" ref={loteDropdownRef}>
                <Input
                  type="text"
                  placeholder="Escribe para buscar un lote..."
                  value={loteSearchTerm}
                  onChange={(e) => {
                    setLoteSearchTerm(e.target.value);
                    setShowLoteDropdown(true);
                  }}
                  onFocus={() => setShowLoteDropdown(true)}
                  data-testid="input-upload-lote"
                  className="pr-8"
                />
                {uploadLote && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      setUploadLote("");
                      setLoteSearchTerm("");
                      setUploadContenedor("");
                      setUploadAwb("");
                      setUploadBl("");
                    }}
                  >
                    ×
                  </button>
                )}
                {showLoteDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filters.lotes
                      .filter(l => 
                        l.value.toLowerCase().includes(loteSearchTerm.toLowerCase()) ||
                        (l.contenedor && l.contenedor.toLowerCase().includes(loteSearchTerm.toLowerCase()))
                      )
                      .slice(0, 20)
                      .map((l) => (
                        <div
                          key={l.value}
                          className={`px-3 py-2 cursor-pointer hover:bg-slate-100 ${uploadLote === l.value ? 'bg-blue-50 text-blue-700' : ''}`}
                          onClick={() => {
                            handleLoteSelect(l.value);
                            setLoteSearchTerm(l.value);
                            setShowLoteDropdown(false);
                          }}
                        >
                          <span className="font-medium">{l.value}</span>
                          {l.contenedor && (
                            <span className="text-gray-500 text-sm ml-2">({l.contenedor})</span>
                          )}
                        </div>
                      ))
                    }
                    {filters.lotes.filter(l => 
                      l.value.toLowerCase().includes(loteSearchTerm.toLowerCase()) ||
                      (l.contenedor && l.contenedor.toLowerCase().includes(loteSearchTerm.toLowerCase()))
                    ).length === 0 && (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No se encontraron lotes que coincidan
                      </div>
                    )}
                  </div>
                )}
              </div>
              {uploadLote && (
                <p className="text-xs text-green-600">Lote seleccionado: {uploadLote}</p>
              )}
            </div>
            
            {/* Campos auto-rellenados desde el lote (solo lectura) */}
            {uploadLote && (
              <div className="grid grid-cols-3 gap-3 bg-slate-50 p-3 rounded-md">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Contenedor</Label>
                  <p className="text-sm font-medium">{uploadContenedor || "-"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">AWB</Label>
                  <p className="text-sm font-medium">{uploadAwb || "-"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">B/L</Label>
                  <p className="text-sm font-medium">{uploadBl || "-"}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Tipo de Documento *</Label>
              <Select value={uploadTipoDoc} onValueChange={setUploadTipoDoc}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDocumento.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Archivo *</Label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                required
                data-testid="input-upload-file"
              />
            </div>
            <p className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
              📝 El nombre del documento se generará automáticamente con el formato:<br/>
              <span className="font-mono text-xs">AAAAMMDD [Tipo] [Factura] [Lote] [BL] [Contenedor].ext</span>
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setUploadDialogOpen(false); resetUploadForm(); }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={uploading || !uploadLote || !uploadTipoDoc}
                data-testid="submit-upload"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Subir
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de renombrar */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nuevo nombre</Label>
              <Input
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                placeholder="Nombre del documento"
                data-testid="input-rename"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRenameDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                className="bg-slate-900 hover:bg-slate-800"
                onClick={handleRename}
                disabled={!newFileName.trim()}
                data-testid="submit-rename"
              >
                Guardar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de eliminar */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span>El documento &quot;{selectedDoc?.nombre_archivo}&quot; será movido a la papelera.</span>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-blue-700 text-sm">
                    Podrás restaurarlo desde la papelera durante los próximos 30 días.
                  </span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Mover a Papelera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de restaurar documento */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-600">
              <RotateCcw className="w-5 h-5" />
              ¿Restaurar documento?
            </AlertDialogTitle>
            <AlertDialogDescription>
              El documento &quot;{selectedDoc?.nombre_archivo}&quot; será restaurado 
              y volverá a estar disponible en la gestión documental.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-restore"
            >
              Restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de eliminar permanentemente */}
      <AlertDialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              ¿Eliminar permanentemente?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                <p>
                  El documento &quot;{selectedDoc?.nombre_archivo}&quot; será eliminado 
                  de forma permanente.
                </p>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-red-700 font-medium text-sm">
                    Esta acción no se puede deshacer.
                  </p>
                  <p className="text-red-600 text-xs mt-1">
                    El archivo físico y todos sus datos serán eliminados permanentemente del sistema.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePermanentDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-permanent-delete"
            >
              Eliminar Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmación de sobreescritura */}
      <AlertDialog open={overwriteDialogOpen} onOpenChange={setOverwriteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Documento existente
            </AlertDialogTitle>
            <AlertDialogDescription>
              Ya existe un documento de este tipo para el lote seleccionado:
              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="font-medium text-slate-700">{existingDocument?.nombre_archivo}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Subido por: {existingDocument?.subido_por_nombre || "Sistema"}
                </p>
              </div>
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-700 text-sm">
                  El documento actual será movido a la papelera y podrá ser recuperado durante 30 días.
                </p>
              </div>
              <p className="mt-3 font-medium">¿Está seguro de que desea sobreescribir el documento existente?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleOverwriteCancel}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleOverwriteConfirm}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Sí, sobreescribir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Previsualización */}
      <Dialog open={previewDialogOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-5xl h-[85vh] p-0">
          <DialogHeader className="p-4 border-b flex-row items-center justify-between">
            <div>
              <DialogTitle className="text-lg">{previewDoc?.nombre_archivo || "Documento"}</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                {previewDoc?.tipo_documento_label || previewDoc?.tipo_documento}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleDownload(previewDoc)}>
                <Download className="w-4 h-4 mr-2" />
                Descargar
              </Button>
              <Button variant="ghost" size="icon" onClick={closePreview}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden h-full">
            {previewUrl && previewDoc?.tipo_archivo?.startsWith("image/") ? (
              <div className="w-full h-full flex items-center justify-center bg-slate-100 p-4">
                <img 
                  src={previewUrl} 
                  alt={previewDoc?.nombre_archivo} 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                className="w-full h-full border-0"
                title={previewDoc?.nombre_archivo}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
